{
  "status": {
    "IN_PROGRESS": "In progress",
    "COMPLETED": "Completed"
  },
  "periodRange": "{{from}} – {{to}}",
  "fields": {
    "number": "Number",
    "account": "Account",
    "reference": "Bank reference",
    "period": "Period",
    "periodStart": "Period from",
    "periodEnd": "Period to",
    "opening": "Opening balance",
    "closing": "Closing balance",
    "matched": "Matched",
    "status": "Status",
    "date": "Date",
    "description": "Description",
    "amount": "Amount",
    "moneyIn": "Money in",
    "moneyOut": "Money out"
  },
  "list": {
    "title": "Bank reconciliation",
    "subtitle": "Bank statements matched against the books, account by account.",
    "new": "New statement",
    "searchLabel": "Search statements",
    "searchPlaceholder": "Search number or bank reference…",
    "accountFilter": "Account",
    "allAccounts": "All accounts",
    "statusFilter": "Status",
    "allStatuses": "All statuses",
    "matchedOf": "{{matched}} of {{lines}}",
    "emptyTitle": "No bank statements yet.",
    "emptyBody": "Enter your bank's statement to reconcile the account with the books.",
    "stats": {
      "label": "Statement figures",
      "inProgress": "In progress",
      "matchedDetail": "{{matched}} of {{lines}} lines matched",
      "matchedShare": "{{percent}}% of their lines matched",
      "toMatch": "Lines to match",
      "toMatchDetail": "In {{moneyIn}} · out {{moneyOut}}",
      "completed": "Completed",
      "reconciledThrough": "Reconciled through {{date}}",
      "noneCompleted": "None completed yet"
    }
  },
  "new": {
    "title": "New bank statement",
    "subtitle": "Enter the period and balances from the bank's statement, then import or type its lines.",
    "sections": {
      "statement": "Statement",
      "statementHint": "As printed on the bank's statement.",
      "lines": "Lines",
      "linesHint": "Import the bank's CSV file or type the lines. You can also add lines later."
    },
    "chooseAccount": "Choose the bank account",
    "noAccounts": "No bank, card or wallet account yet. Ask an administrator to add one under Accounting › Cash & bank.",
    "referenceHint": "The bank's statement number, if it has one.",
    "openingHint": "An account's first statement opens at any balance; later ones open at the previous closing balance.",
    "save": "Create statement",
    "created": "Statement {{number}} created",
    "closingOff": "Opening plus the lines doesn't come to this.",
    "outOfBalance": "The statement doesn't balance: opening plus the lines differs from the closing balance by {{difference}}. Check the balances or the lines.",
    "lineIssue": "Line {{line}}: {{message}}",
    "blockers": {
      "syntax": "The file can't be read: fix it or choose another.",
      "overLimit": "A statement holds at most 2,000 lines: split it by period.",
      "manual": "Some typed lines are incomplete: give each a date in the period and one amount.",
      "noRows": "The file has no lines to import.",
      "errors_one": "{{count}} row of the file has an error: fix the column settings, or leave it out.",
      "errors_other": "{{count}} rows of the file have errors: fix the column settings, or leave them out."
    }
  },
  "lines": {
    "source": "Where the lines come from",
    "fromFile": "Import a CSV file",
    "typed": "Type the lines",
    "typedLabel": "Statement lines",
    "add": "Add line",
    "remove": "Remove line {{n}}",
    "actions": "Actions",
    "total": "Total",
    "enterHint": "Enter on the last line adds a new one.",
    "dateOf": "Date of line {{n}}",
    "descriptionOf": "Description of line {{n}}",
    "referenceOf": "Reference of line {{n}}",
    "moneyInOf": "Money in on line {{n}}",
    "moneyOutOf": "Money out on line {{n}}",
    "issues": {
      "date": "Give the date.",
      "outsidePeriod": "The date is outside the statement period.",
      "amount": "Enter the money in or the money out.",
      "both": "Enter either money in or money out, not both."
    }
  },
  "csv": {
    "choose": "Choose CSV file…",
    "chooseAnother": "Choose another file…",
    "fileLabel": "Statement file (CSV)",
    "hint": "The bank's CSV export: one row per transaction, at most {{max}} rows.",
    "fileError": {
      "tooLarge": "The file is larger than 5 MB, so it is not a bank statement.",
      "unreadable": "The file couldn't be read. Try again."
    },
    "syntaxTitle": "The file isn't valid CSV",
    "syntax": {
      "unterminatedQuote": "Line {{line}}: a quoted field is never closed.",
      "strayQuote": "Line {{line}}: a quote appears inside an unquoted field.",
      "textAfterQuote": "Line {{line}}: there is text after a closing quote."
    },
    "settings": "File settings",
    "delimiter": "Separator",
    "delimiters": {
      "comma": "Comma (,)",
      "semicolon": "Semicolon (;)",
      "tab": "Tab"
    },
    "encoding": "Encoding",
    "encodingHint": "Choose Arabic (Windows) if Arabic text shows as symbols.",
    "encodings": {
      "utf-8": "Unicode (UTF-8)",
      "windows-1256": "Arabic (Windows-1256)"
    },
    "skipRows": "Rows to skip at the top",
    "skipRowsHint": "Title lines above the column headings.",
    "hasHeader": "The first row names the columns",
    "dateColumn": "Date column",
    "dateFormat": "Date format",
    "chooseColumn": "Choose a column",
    "chooseFormat": "Choose the format",
    "fits": "The dates read as: {{formats}}",
    "fitsNone": "No format reads every date: check the date column.",
    "formats": {
      "dmy": "dd/mm/yyyy",
      "mdy": "mm/dd/yyyy",
      "ymd": "yyyy-mm-dd"
    },
    "descriptionColumn": "Description column",
    "referenceColumn": "Reference column",
    "noColumn": "None",
    "column": "Column {{n}}",
    "columnNamed": "Column {{n}} · {{name}}",
    "layout": "Amounts",
    "layouts": {
      "signed": "One amount column (signed)",
      "split": "Separate debit and credit columns"
    },
    "amountColumn": "Amount column",
    "debitColumn": "Debit column",
    "creditColumn": "Credit column",
    "sign": "Sign convention",
    "signs": {
      "signed": {
        "standard": "Positive amounts are money in (deposits)",
        "reversed": "Positive amounts are money out (withdrawals)"
      },
      "split": {
        "standard": "Credits are money in, debits money out (the bank's view)",
        "reversed": "Debits are money in, credits money out"
      }
    },
    "summary": "Rows read: {{rows}} · ready to import: {{valid}}",
    "deposits": "Money in",
    "withdrawals": "Money out",
    "previewLabel": "Rows to import",
    "fileLine": "Row",
    "previewCapped": "Showing {{shown}} of {{total}} rows (errors first); every valid row is imported.",
    "issues": {
      "date": {
        "missing": "No date.",
        "invalid": "The date doesn't read in the chosen format.",
        "noFormat": "Choose the date format.",
        "outsidePeriod": "The date is outside the statement period."
      },
      "amount": {
        "missing": "No amount.",
        "zero": "The amount is zero.",
        "both": "Both debit and credit are filled in.",
        "invalid": "The amount isn't a number.",
        "tooManyDecimals": "The amount has more than 3 decimals.",
        "tooLarge": "The amount is too large."
      },
      "description": {
        "truncated": "Description shortened to 250 characters."
      },
      "reference": {
        "truncated": "Reference shortened to 100 characters."
      }
    },
    "loaded_one": "{{name}}: {{count}} row",
    "loaded_other": "{{name}}: {{count}} rows",
    "withErrors_one": "{{count}} with an error",
    "withErrors_other": "{{count}} with errors",
    "skipBad_one": "Leave out the row with an error",
    "skipBad_other": "Leave out the {{count}} rows with errors"
  },
  "balance": {
    "opening": "Opening",
    "closing": "Closing",
    "difference": "Difference",
    "balanced": "Balances",
    "noLines": "No lines yet: checked when you complete.",
    "enterBalances": "Enter the opening and closing balances.",
    "lines_one": "{{count}} line",
    "lines_other": "{{count}} lines"
  },
  "page": {
    "autoMatch": "Auto-match",
    "addLines": "Add lines",
    "editHeader": "Edit details",
    "complete": "Complete",
    "reopen": "Reopen",
    "bankReference": "Bank ref. {{reference}}",
    "tabs": "Statement views",
    "tabReconcile": "Matching",
    "tabReport": "Reconciliation report",
    "tabAttachments": "Attachments",
    "blockers": {
      "outOfBalance": "Complete is offered once the statement balances: opening plus the lines is {{difference}} off the closing balance.",
      "unmatched_one": "Complete is offered once every line is matched: {{count}} line is left.",
      "unmatched_other": "Complete is offered once every line is matched: {{count}} lines are left."
    }
  },
  "summary": {
    "deposits": "Money in",
    "withdrawals": "Money out",
    "statementDifference": "Statement check"
  },
  "workspace": {
    "keys": "↑/↓ move · Space selects · Enter matches · Esc clears",
    "statementLines": "Statement lines",
    "unmatchedOf": "{{unmatched}} of {{lines}} to match",
    "noLines": "No lines on this statement yet.",
    "noLinesBody": "Add them from the bank's file or type them in.",
    "selected": "Selected",
    "match": "Match",
    "actions": "Actions",
    "lineLabel": "Line {{n}} · {{date}} · {{description}} · {{amount}}",
    "unmatched": "Unmatched",
    "unmatchLine": "Unmatch line {{n}}",
    "adjustLine": "Post an adjustment for line {{n}}",
    "deleteLine": "Delete line {{n}}",
    "book": "Book entries",
    "bookFor": "Candidates for line {{n}}",
    "window": "Date window",
    "pickLine": "Choose an unmatched line",
    "pickLineBody": "Its book candidates appear here, exact amounts first.",
    "noCandidates": "No book entries in this window.",
    "noCandidatesBody": "Widen the window, or post an adjustment if only the bank has the line.",
    "entry": "Journal entry",
    "bookLabel": "Entry {{entry}} · {{date}} · {{amount}}",
    "exact": "Exact amount",
    "selection": "Selection",
    "difference": "Difference",
    "clear": "Clear",
    "matchAction": "Match",
    "kind": {
      "MATCH": "Matched",
      "ADJUSTMENT": "Adjustment"
    },
    "windowDays_one": "± {{count}} day",
    "windowDays_other": "± {{count}} days",
    "selectedLines_one": "Statement: {{count}} line",
    "selectedLines_other": "Statement: {{count}} lines",
    "selectedBook_one": "Book: {{count}} entry",
    "selectedBook_other": "Book: {{count}} entries",
    "matched_one": "{{count}} line matched",
    "matched_other": "{{count}} lines matched"
  },
  "header": {
    "title": "Edit {{number}}",
    "body": "The account never changes, and the lines must stay inside the period.",
    "save": "Save details",
    "saved": "Statement details saved",
    "conflictTitle": "Someone else changed this statement",
    "conflictBody": "Reload to see their changes, then make yours again.",
    "reload": "Reload"
  },
  "addLines": {
    "title": "Add lines to {{number}}",
    "body": "Lines dated {{from}} to {{to}}.",
    "none": "There are no lines to add.",
    "save_one": "Add {{count}} line",
    "save_other": "Add {{count}} lines",
    "done_one": "{{count}} line added",
    "done_other": "{{count}} lines added"
  },
  "adjust": {
    "title": "Adjustment for line {{n}}",
    "body": "For a line only the bank has, such as charges or interest.",
    "account": "Post against",
    "accountHint": "An expense or asset account; never a cash, bank or control account.",
    "chooseAccount": "Choose the account",
    "searchAccounts": "Search account code or name…",
    "noAccounts": "No account matches.",
    "noAccess": "You can't list the accounts to post to. Ask for access to the ledger.",
    "postsTitle": "A journal entry will be posted",
    "postsIn": "Dated {{date}}: {{amount}} into the bank account, credited to the chosen account, reference {{number}}. It is matched to this line; unmatching it reverses the entry.",
    "postsOut": "Dated {{date}}: {{amount}} out of the bank account, charged to the chosen account, reference {{number}}. It is matched to this line; unmatching it reverses the entry.",
    "save": "Post and match",
    "done": "Adjustment posted for line {{n}}"
  },
  "auto": {
    "title": "Auto-match",
    "body": "Pairs each statement line with the one book entry of the same amount and direction within the window. Lines with several possible entries are left for you.",
    "window": "Date window",
    "windowHint": "How many days a book entry may be from the bank's date.",
    "run": "Auto-match",
    "resultTitle": "Auto-match finished",
    "result_one": "Matched {{matched}}; {{count}} line is still unmatched.",
    "result_other": "Matched {{matched}}; {{count}} lines are still unmatched."
  },
  "complete": {
    "title": "Complete {{number}}?",
    "body": "The statement balances and every line is matched. Once completed, its lines and matches are locked; only someone allowed to reopen statements can change it again.",
    "confirm": "Complete statement",
    "done": "Statement {{number}} completed"
  },
  "reopen": {
    "title": "Reopen {{number}}?",
    "body": "Only the account's latest statement can be reopened. Its lines and matches can then be changed again.",
    "reason": "Reason",
    "confirm": "Reopen",
    "done": "Statement {{number}} reopened"
  },
  "unmatch": {
    "title": "Unmatch line {{lines}}?",
    "body_one": "The statement line ({{total}}) and its book entries become unmatched again.",
    "body_other": "The {{count}} statement lines ({{total}}) and their book entries become unmatched again.",
    "adjustmentBody": "The adjustment entry {{entry}} is reversed on its own date, and the line becomes unmatched.",
    "confirm": "Unmatch",
    "done": "Unmatched"
  },
  "deleteLine": {
    "title": "Delete line {{n}}?",
    "body": "The line is removed from the statement. Only unmatched lines can be deleted.",
    "confirm": "Delete line",
    "done": "Line {{n}} deleted"
  },
  "report": {
    "title": "Bank reconciliation",
    "subtitle": "{{account}} · {{number}} · as of {{date}}",
    "print": "Print",
    "bookBalance": "Balance per books at {{date}}",
    "lessDeposits": "Less deposits not yet on the statement",
    "addPayments": "Add payments the bank has not cleared yet",
    "addClearedAhead": "Add items the bank cleared before they were booked",
    "lessOpeningItems": "Less opening items not yet cleared",
    "adjustedBook": "Adjusted book balance",
    "closing": "Balance per bank statement",
    "difference": "Difference",
    "reconciled": "Reconciled: the adjusted book balance equals the bank's.",
    "notReconciled": "The adjusted book balance differs from the bank's: match the remaining lines or post adjustments.",
    "startingDifference": "Starting difference",
    "startingExplained": "Items booked before {{date}}, when reconciliation began, that the bank had not cleared yet; they drop out as they are matched.",
    "outstandingDeposits": "Deposits not yet on the statement",
    "outstandingPayments": "Payments the bank has not cleared yet",
    "clearedAhead": "Cleared by the bank before they were booked",
    "none": "None.",
    "total": "Total",
    "completedAt": "Completed {{time}}.",
    "inProgress": "In progress: the figures change as lines are matched."
  },
  "errors": {
    "STATEMENT_OUT_OF_BALANCE": "Opening plus the lines doesn't come to the closing balance. Check the balances and the lines.",
    "STATEMENT_COMPLETED": "This statement is completed; reopen it before changing it.",
    "MATCH_UNBALANCED": "The selected statement lines and book entries don't come to the same amount.",
    "UNMATCHED_LINES": "Some statement lines aren't matched yet. Match them, or post an adjustment for bank charges and interest.",
    "NOT_LATEST_STATEMENT": "Only the account's latest statement can be reopened.",
    "PERIOD_NOT_AFTER_PREVIOUS": "The period must start after the previous completed statement ends.",
    "OPENING_BALANCE_MISMATCH": "The opening balance must equal the previous statement's closing balance.",
    "MATCHED_BEFORE_START": "Book entries dated before the new start are already matched; unmatch them first.",
    "LINE_MATCHED": "Unmatch the line before deleting it.",
    "TOO_MANY_LINES": "A statement holds at most 2,000 lines; split it by period."
  }
}
